// The review page's sections, in the order the reviewer works through them. Ported from the prototype page
// (report_summary_analysis/pipeline/render_review.py); the wording there was reviewed, so keep it in step.
import React, { useContext } from "react";
import { LookupContext, Card, Chip, Field, Refs, Rich } from "./parts";
import { MED_STATUS, OUTCOME, OWNER, STATUS, chosenDose, fmtDate, lgKey } from "./logic";

export function Banners({ review }) {
  return (
    <>
      {(review.data_dates || []).length > 0 && (
        <div className="banner">
          <b>Check the dates the numbers cover.</b> The member's data doesn't fill the window, so the report may describe fewer or older days than the dates above.
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {review.data_dates.map((x, i) => <li key={i}>{x[0].toUpperCase() + x.slice(1)}</li>)}
          </ul>
        </div>
      )}
      {review.check_needs_sensor && (
        <div className="banner">
          <b>The seven-day check reads the sensor.</b> Make sure the member will still be wearing one on {fmtDate(review.check_date)}; if not, ask for a check that reads something they log.
        </div>
      )}
      {(review.plan_problems || []).length > 0 && (
        <div className="banner">
          <b>Checks the plan didn't pass.</b> Code checked these after the analyst's one retry; look at each before confirming.
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {review.plan_problems.map((x, i) => (
              <li key={i}><span className="muted">{x.section}:</span> {x.problem}</li>
            ))}
          </ul>
        </div>
      )}
      {(review.typed_numbers || []).length > 0 && (
        <div className="banner">
          <b>Numbers close to the member's data but not the same.</b> Check each one against the value shown: it may be misremembered or rounded.
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {review.typed_numbers.map((x, i) => (
              <li key={i}>
                <span className="muted">{x.section.replace(/_/g, " ")}:</span> {x.text}{" "}
                <b>({x.numbers.join(", ")}{x.close_to ? `; the data has ${x.close_to.join(", ")}` : ""})</b>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

export function Headline({ c }) {
  return (
    <Card section="headline" title="Headline" required done={c.ui.confirmed.headline} onConfirm={(v) => c.confirm("headline", v)} updated={c.updated.has("headline")}
      why="The main message of the report; the whole report is built around it. The member sees these exact words.">
      <Field path="headline" text={c.plan.headline} edited={c.ui.edited.headline} onSave={c.saveField} className="headline" />
    </Card>
  );
}

export function Finding({ c }) {
  const F = c.plan.finding;
  return (
    <Card section="finding" title="The finding" updated={c.updated.has("finding")} why="What the report concludes about what's behind the member's main concern.">
      <Field path="finding.summary" text={F.summary} edited={c.ui.edited["finding.summary"]} onSave={c.saveField} />
      <dl className="kv" style={{ marginTop: 10 }}>
        <dt>Still in play</dt><dd>{F.surviving.join(", ")}</dd>
        <dt>The seven-day check settles</dt><dd>{F.check_tests.join(", ")}</dd>
        {F.others_handled_by && <><dt>The rest</dt><dd>{F.others_handled_by}</dd></>}
      </dl>
    </Card>
  );
}

export function Differential({ c }) {
  const changes = Object.keys(c.statusChanges);
  return (
    <Card section="differential" title="What could be behind it" updated={c.updated.has("differential")}
      why="The candidates, each checked against the member's data. Hover a number to see where it came from. Changing a status updates the parts of the plan that rest on it.">
      <div className="legend">
        <b style={{ color: "var(--ink)", fontWeight: 600 }}>Evidence key</b>
        <span><span className="chip">89 mg/dL</span> computed from their data</span>
        <span><span className="chip small">72 vs 61</span> comparison with under 3 days or meals in a group: a lead, not a finding</span>
        <span><span className="chip fact">A1C 5.6%</span> from notes, intake or labs</span>
        <span><span className="chip quote">“their words”</span> quoted exactly</span>
        <span><span className="chip missing">id: not found</span> doesn't match anything: check it</span>
      </div>
      <div className="tablewrap">
        <table className="dx">
          <colgroup><col style={{ width: "17%" }} /><col style={{ width: "16%" }} /><col style={{ width: "38%" }} /><col style={{ width: "23%" }} /><col style={{ width: "6%" }} /></colgroup>
          <tbody>
            <tr><th>Candidate</th><th>Status</th><th>What we saw</th><th>Evidence</th><th></th></tr>
            {c.plan.differential.map((d, i) => {
              const rm = c.ui.removed[`dx.${i}`];
              return (
                <tr key={i} className={rm ? "removed" : ""}>
                  <td><b>{d.name}</b>{d.is_member_theory && <><br /><span className="tag opt">Member's own theory</span></>}</td>
                  <td>
                    <select className={`status pill s-${d.status}`} value={d.status} onChange={(e) => c.setStatus(i, e.target.value)} aria-label={`Status of ${d.name}`}>
                      {Object.entries(STATUS).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                    </select>
                    {c.statusChanges[d.name] && <><br /><span className="tag edited">Changed</span></>}
                  </td>
                  <td><Field path={`differential.${i}.evidence`} text={d.evidence} edited={c.ui.edited[`differential.${i}.evidence`]} onSave={c.saveField} /></td>
                  <td><Refs ids={d.refs} /></td>
                  <td><button className="rm" onClick={() => c.toggleRemove(`dx.${i}`)}>{rm ? "Restore" : "Remove"}</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {changes.length > 0 && (
        <div className="ripple">
          <span>You changed a status, so the finding may no longer fit. Updating redoes the finding, the one thing to do, the check, the chart and the headline; everything else stays as it is.</span>
          <button className="ghost" onClick={c.updateForStatuses}>Update the plan to match</button>
        </div>
      )}
    </Card>
  );
}

export function Lever({ c }) {
  const lv = c.plan.lever;
  return (
    <Card section="lever" title="The one thing to do this week" required done={c.ui.confirmed.lever} onConfirm={(v) => c.confirm("lever", v)} updated={c.updated.has("lever")}
      why="Seven days, with a box to tick each day. It's the only thing the member is asked to do.">
      {c.review.lever_logging && (
        <div className="banner" style={{ margin: "0 0 8px" }}>This reads as logging only, though it's marked as a change. Check whether it asks the member to do anything differently.</div>
      )}
      {lv.kind === "logging_only" && <span className="tag edited" style={{ marginBottom: 6, display: "inline-block" }}>Logging only: nothing changes yet</span>}
      <Field path="lever.action" text={lv.action} edited={c.ui.edited["lever.action"]} onSave={(_, v) => c.saveLever(v)} className="lever-action" />
      <p className="why" style={{ margin: "4px 0 0" }}>Rewriting the action keeps your words; saving it updates the check, the headline and the notes below to fit (about 25 seconds).</p>
      <dl className="kv" style={{ marginTop: 10 }}>
        {lv.measured_by && <><dt>How we read it</dt><dd><Rich text={lv.measured_by} /></dd></>}
        {lv.kind === "logging_only" && (
          <><dt>Why not a change</dt><dd><Rich text={lv.logging_only_because} /></dd><dt>What the week decides</dt><dd><Rich text={lv.unlocks} /></dd></>
        )}
        <dt>What doesn't change</dt>
        <dd><Field path="lever.what_doesnt_change" text={lv.what_doesnt_change} edited={c.ui.edited["lever.what_doesnt_change"]} onSave={c.saveField} /></dd>
        <dt>Why it's doable for them</dt><dd><Rich text={lv.friction_gate} /></dd>
        <dt>What it can move</dt><dd><Rich text={lv.affects} /></dd>
        {lv.rejected_alternatives.length > 0 && <><dt>Considered instead</dt><dd>{lv.rejected_alternatives.map((t, i) => <div key={i}>{t}</div>)}</dd></>}
      </dl>
    </Card>
  );
}

export function Check({ c }) {
  const ck = c.plan.check;
  return (
    <Card section="check" title="Seven-day check" required done={c.ui.confirmed.check} onConfirm={(v) => c.confirm("check", v)} updated={c.updated.has("check")}
      why={`How we'll know whether the finding holds, read on ${fmtDate(c.stage.check_date)} from data the member already generates.`}>
      <Field path="check.criteria" text={ck.criteria} edited={c.ui.edited["check.criteria"]} onSave={c.saveField} />
      <dl className="kv" style={{ marginTop: 10 }}>
        <dt>Reads from</dt><dd>{ck.reads_from.map((r, i) => <React.Fragment key={i}>{i > 0 && ", "}<Rich text={r} /></React.Fragment>)}</dd>
        <dt>If we were wrong</dt><dd><Field path="check.failure_clause" text={ck.failure_clause} edited={c.ui.edited["check.failure_clause"]} onSave={c.saveField} /></dd>
      </dl>
    </Card>
  );
}

export function DoctorAsk({ c }) {
  const da = c.plan.doctor_ask;
  const allGone = da.present && da.items.length > 0 && da.items.every((_, i) => c.ui.removed[`da.${i}`]);
  if (!da.present)
    return (
      <Card section="doctor_ask" title="For their doctor" updated={c.updated.has("doctor_ask")}>
        <p className="why" style={{ margin: 0 }}>No doctor items in this report.{da.no_ask_because && <> Why: <Rich text={da.no_ask_because} /></>}</p>
      </Card>
    );
  return (
    <Card section="doctor_ask" title="For their doctor" required={!allGone} done={c.ui.confirmed.doctor_ask} onConfirm={(v) => c.confirm("doctor_ask", v)} updated={c.updated.has("doctor_ask")}
      why={allGone ? "Every item is removed, so the report has no doctor section." : "Things for the member to raise with their doctor. Included only when it gives them something they don't already have."}>
      <ul className="items">
        {da.items.map((t, i) => {
          const rm = c.ui.removed[`da.${i}`];
          return (
            <li key={i} className={rm ? "removed" : ""}>
              <div className="grow">
                {typeof t === "string" ? (
                  <Field path={`doctor_ask.items.${i}`} text={t} edited={c.ui.edited[`doctor_ask.items.${i}`]} onSave={c.saveField} />
                ) : (
                  <>
                    <Field path={`doctor_ask.items.${i}.say`} text={t.say} edited={c.ui.edited[`doctor_ask.items.${i}.say`]} onSave={c.saveField} />
                    <div style={{ marginTop: 4 }}><Refs ids={t.refs} /></div>
                  </>
                )}
              </div>
              <button className="rm" onClick={() => c.toggleRemove(`da.${i}`)}>{rm ? "Restore" : "Remove"}</button>
            </li>
          );
        })}
      </ul>
      <dl className="kv" style={{ marginTop: 10 }}>
        <dt>Why it's worth asking</dt><dd><Rich text={da.passes_test_because} /></dd>
        <dt>Urgency</dt><dd>{{ none: "None", this_week: "This week", next_visit: "Next visit" }[da.urgency]}</dd>
      </dl>
    </Card>
  );
}

// Medications: a status per entry, changeable. A dose the notes give differently is flagged on its row, and the
// reviewer picks which is current. The report treats only Current ones as something the member takes.
export function Medications({ c }) {
  const MEDS = c.review.meds || {};
  if (!Object.keys(MEDS).length) return null;
  const given = Object.fromEntries((c.plan.medications || []).map((m) => [m.id, m]));
  // the two most telling mentions show; the rest fold away (a long note can name a drug 20 times)
  const rank = (r) => (r.hint === "current" ? 0 : 4) + (r.named === false ? 2 : 0) + (/\d\s*(mg|units?|mcg)/i.test(r.excerpt) ? 0 : 1) + (r.excerpt.split(" ").length < 4 ? 1 : 0);
  const Line = ({ r }) => (
    <div className="muted" style={{ marginTop: 3 }}>
      {r.source_id}{r.under && <> · under “{r.under.split(" / ").pop().replace(/^[\d.\s#-]+/, "")}”</>}: <i>“{r.excerpt.replace(/^[-*•\s]+/, "")}”</i>
    </div>
  );
  return (
    <Card section="meds" title="Medications" required done={c.ui.confirmed.meds} onConfirm={(v) => c.confirm("meds", v)}
      why="From the app's medicines list and the notes. The report speaks of only the Current ones as something the member takes; insulin and sulfonylurea rules apply to anything not marked Past or Planned.">
      <div style={{ overflowX: "auto" }}>
        <table className="dx">
          <tbody>
            <tr><th>Medication</th><th>Found in</th><th>Status</th><th>What the sources say</th></tr>
            {Object.entries(MEDS).map(([id, m]) => {
              const g = given[id] || {};
              const st = c.ui.medStatus[id] || g.status || "unclear";
              const ms = [...(m.mentioned_in || [])].sort((a, b) => rank(a) - rank(b));
              const conflict = (c.review.dose_conflicts || []).find((x) => x.id === id);
              const ch = c.ui.doseChoice[id] || {};
              return (
                <tr key={id}>
                  <td>
                    <b>{m.name}</b>
                    {m.dose && <><br /><span className="muted">{m.dose}{m.schedule ? ` · ${m.schedule}` : ""}</span></>}
                    {conflict && (
                      <>
                        <br />
                        <span className="dose-diff" title={`${conflict.source_id}: “${conflict.excerpt}”`}>
                          ⚠ Dose differs: {conflict.source_id}{conflict.note_date ? ` (${fmtDate(conflict.note_date)})` : ""} says {conflict.notes}
                        </span>
                        {conflict.app_newer && (
                          <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
                            The app entry ({fmtDate(conflict.app_since)}) is newer than the note: possibly a dose change since the call.
                          </div>
                        )}
                        <div className="dose-pick">
                          <span>Which is current?</span>
                          {[["app", `${conflict.app} (app)`], ["notes", `${conflict.notes} (notes)`], ["other", "Other"]].map(([v, t]) => (
                            <label key={v}><input type="radio" name={`dose-${id}`} checked={ch.pick === v} onChange={() => c.pickDose(id, v)} /> {t}</label>
                          ))}
                          {ch.pick === "other" && (
                            <input className="dose-other" placeholder="e.g. 5 mg weekly" value={ch.other || ""} onChange={(e) => c.setDoseOther(id, e.target.value)} aria-label={`Dose for ${m.name}`} />
                          )}
                          {chosenDose(conflict, ch) && <span className="muted" style={{ fontWeight: 400 }}>The report prints {chosenDose(conflict, ch)}.</span>}
                        </div>
                      </>
                    )}
                    {m.effect && <><br /><span className="tag opt">{m.effect}</span></>}
                  </td>
                  <td>{m.on_app_list ? <span className="tag ok">App list</span> : m.source === "intake" ? <span className="tag opt">Intake</span> : <span className="tag req">Notes only</span>}</td>
                  <td>
                    <select value={st} onChange={(e) => c.setMed(id, e.target.value)} aria-label={`Status of ${m.name}`}>
                      {Object.entries(MED_STATUS).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                    </select>
                    {c.ui.medStatus[id] && <><br /><span className="tag edited">Changed</span></>}{" "}
                    {m.hint && g.status && m.hint !== g.status && !m.on_app_list && <span className="tag edited">Code's read: {MED_STATUS[m.hint] || m.hint}</span>}
                    {!given[id] && <><br /><span className="tag req">No status from the analyst</span></>}
                  </td>
                  <td>
                    {g.excerpt && g.source_id !== "medicines" && (
                      <div style={{ marginTop: 3 }}><span className="muted">Status from {g.source_id}:</span> <i>“{g.excerpt}”</i></div>
                    )}
                    {ms.slice(0, 2).map((r, i) => <Line key={i} r={r} />)}
                    {ms.length > 2 && (
                      <details className="more">
                        <summary>{ms.length - 2} more mention{ms.length > 3 ? "s" : ""}</summary>
                        {ms.slice(2).map((r, i) => <Line key={i} r={r} />)}
                      </details>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function Safety({ c }) {
  const S = c.plan.safety;
  const all = S.length > 0 && S.every((_, i) => c.ui.confirmed[`safety.${i}`]);
  return (
    <section data-section="safety" className={`card ${S.length ? "req" : ""} ${all ? "done" : ""} ${c.updated.has("safety") ? "changed" : ""}`}>
      <h2>Safety{c.updated.has("safety") && <span className="tag upd">Updated</span>}</h2>
      {S.length ? (
        <>
          <p className="why">Each one needs your confirmation.</p>
          <ul className="items">
            {S.map((s, i) => (
              <li key={i}>
                <input type="checkbox" checked={!!c.ui.confirmed[`safety.${i}`]} onChange={(e) => c.confirm(`safety.${i}`, e.target.checked)} aria-label="Confirm safety item" />
                <div className="grow">
                  <b><Rich text={s.noticed} /></b><br />
                  <Rich text={s.why_it_matters} /><br />
                  <span className="muted">Next step:</span> <Rich text={s.next_step} /> · <span className="muted">{s.urgency.replace(/_/g, " ")}</span> <Refs ids={s.refs} />
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="why" style={{ margin: 0 }}>No safety items flagged. If you see one the report should include, describe it under Plan feedback below.</p>
      )}
    </section>
  );
}

export function LongerGame({ c }) {
  const G = c.plan.longer_game;
  return (
    <section data-section="longer_game" className={`card ${G.some((i) => i.owner === "member") ? "req" : ""} ${c.updated.has("longer_game") ? "changed" : ""}`}>
      <h2>The plan ahead{c.updated.has("longer_game") && <span className="tag upd">Updated</span>}</h2>
      <p className="why">Steps over the next 8 to 12 weeks, in order. Confirm the member's own items; care-team items are commitments for the team.</p>
      <ul className="items">
        {G.map((it, i) => {
          const k = `lg.${i}`, ck = lgKey(it), rm = c.ui.removed[k];
          return (
            <li key={i} className={rm ? "removed" : ""}>
              {it.owner === "member" && !rm ? (
                <input type="checkbox" title="Confirm" checked={!!c.ui.confirmed[ck]} onChange={(e) => c.confirm(ck, e.target.checked)} aria-label="Confirm plan item" />
              ) : <span style={{ width: 16 }} />}
              <div className="grow">
                <Field path={`longer_game.${i}.item`} text={it.item} edited={c.ui.edited[`longer_game.${i}.item`]} onSave={c.saveField} />
                <div style={{ marginTop: 3 }}>
                  <span className={`owner ${it.owner}`}>{OWNER[it.owner]}</span>
                </div>
              </div>
              <button className="rm" onClick={() => c.toggleRemove(k)}>{rm ? "Restore" : "Remove"}</button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function People({ c }) {
  const P = c.plan.people_mentioned;
  const all = P.length > 0 && P.every((_, i) => c.ui.confirmed[`person.${i}`]);
  return (
    <section data-section="people_mentioned" className={`card ${P.length ? "req" : ""} ${all ? "done" : ""} ${c.updated.has("people_mentioned") ? "changed" : ""}`}>
      <h2>People named in the report{c.updated.has("people_mentioned") && <span className="tag upd">Updated</span>}</h2>
      <p className="why">Everyone the report names. Anyone the member is pointed to for what comes next must be on their care team.</p>
      <ul className="items">
        {P.length ? P.map((p, i) => (
          <li key={i}>
            <input type="checkbox" checked={!!c.ui.confirmed[`person.${i}`]} onChange={(e) => c.confirm(`person.${i}`, e.target.checked)} aria-label={`Confirm ${p.name}`} />
            <div className="grow">
              <b>{p.name}</b> · {p.role} {p.forward_looking && <span className="tag opt">Next steps</span>}<br />
              <span className="muted">{p.context}</span>
            </div>
          </li>
        )) : <li className="muted">No one is named; the report says "your care team".</li>}
      </ul>
      <p className="why" style={{ margin: "10px 0 0" }}>Signed "Heald Care Team" on every report.</p>
    </section>
  );
}

export function Placeholders({ c }) {
  const P = c.plan.placeholders;
  return (
    <Card section="placeholders" title="Details to fill in" updated={c.updated.has("placeholders")}
      why="Things the report needs that aren't in the data. Optional ones can stay empty; the report is written without them.">
      {P.length ? P.map((p, i) => (
        <div key={p.id || i} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <label style={{ minWidth: 170 }} htmlFor={`ph-${i}`}>
            {p.name} {p.required ? <span className="tag req">Required</span> : <span className="tag opt">Optional</span>}
          </label>
          <input id={`ph-${i}`} type="text" value={p.value || ""} placeholder={p.required ? "Required" : "Leave empty if not known yet"}
            onChange={(e) => c.setPlaceholder(i, e.target.value)} style={{ flex: 1, minWidth: 200 }} />
        </div>
      )) : <p className="muted" style={{ margin: 0 }}>Nothing to fill in.</p>}
    </Card>
  );
}

export function PageOne({ c }) {
  const L = useContext(LookupContext);
  const P = c.plan;
  const short = (ref) => (L[ref] ? (L[ref].short.length > 28 ? `${L[ref].short.slice(0, 28)}…` : L[ref].short) : ref);
  return (
    <details data-section="vitals" className={["vitals", "credit", "chart", "belief", "questions_answered"].some((s) => c.updated.has(s)) ? "changed" : ""}>
      <summary>Page 1 numbers, credit and chart</summary>
      <div className="inner">
        <div className="vitals">
          {P.vitals.map((v, i) => <div key={i} className={`vital ${v.win ? "win" : ""}`}><b>{short(v.ref)}</b>{v.label}</div>)}
        </div>
        <p style={{ margin: "14px 0 4px" }}><b>Already working (credit)</b></p>
        {P.credit.map((x, i) => <div key={i}><Rich text={x.text} /> <Refs ids={x.refs} /></div>)}
        <p style={{ margin: "14px 0 4px" }}><b>Chart</b>: <Rich text={P.chart.shows} /></p>
        {c.review.chart_svg ? (
          // generated by the service from the member's readings (chart_preview.svg), not from user input
          <div className="chartbox" dangerouslySetInnerHTML={{ __html: c.review.chart_svg }} />
        ) : <p className="muted">Preview not available for the {P.chart.chart.replace(/_/g, " ")} chart yet.</p>}
        <p className="muted" style={{ margin: 0 }}>Caption caveat: {P.chart.caveat}</p>
        {P.belief && (
          <>
            <p style={{ margin: "14px 0 4px" }}><b>Belief to correct</b></p>
            <div>
              <Rich text={P.belief.held_belief} /> (<Rich text={P.belief.evidence} />)<br />
              True half: {P.belief.true_half}<br />Conclusion to retire: {P.belief.retired_conclusion}
            </div>
          </>
        )}
        {P.questions_answered.length > 0 && (
          <>
            <p style={{ margin: "14px 0 4px" }}><b>Their questions, answered</b></p>
            {P.questions_answered.map((q, i) => <div key={i}><Chip id={q.question_quote_id} /> {q.answer}</div>)}
          </>
        )}
      </div>
    </details>
  );
}

export function Evidence({ c }) {
  const L = useContext(LookupContext);
  const P = c.plan;
  return (
    <details>
      <summary>Evidence: comparisons, facts and quotes</summary>
      <div className="inner">
        <p className="why" style={{ marginTop: 0 }}>Every comparison the analysis ran, including ones that found nothing or went against a candidate.</p>
        <div className="tablewrap">
          <table>
            <tbody>
              <tr><th>What was compared</th><th>Testing</th><th>Result</th><th>Outcome</th></tr>
              {P.comparisons.length ? P.comparisons.map((x, i) => (
                <tr key={i}>
                  <td>{L[x.result_id] ? L[x.result_id].desc : x.result_id}</td>
                  <td>{x.tests}</td>
                  <td><Chip id={x.result_id} /></td>
                  <td>
                    <span className={`pill o-${x.outcome}`}>{OUTCOME[x.outcome]}</span>
                    {!x.used_in.length && <><br /><span className="muted" style={{ fontSize: 12 }}>not used in the report</span></>}
                  </td>
                </tr>
              )) : <tr><td colSpan={4} className="muted">No comparisons run.</td></tr>}
            </tbody>
          </table>
        </div>
        <p style={{ margin: "16px 0 6px" }}><b>Facts</b> <span className="muted">(copied from notes, intake and labs)</span></p>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {P.facts.map((f) => (
            <li key={f.id}>
              {f.text}
              {f.excerpt ? <><br /><span className="muted">{L[f.id] ? L[f.id].where : f.source_id} says:</span> <i>“{f.excerpt}”</i></> : <span className="muted"> · {f.source_id}</span>}
            </li>
          ))}
        </ul>
        <p style={{ margin: "16px 0 6px" }}><b>Their words</b> <span className="muted">(quoted exactly)</span></p>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {P.quotes.map((q) => <li key={q.id}>“{q.text}” <span className="muted">· {q.use}{q.about ? `, ${q.about}` : ""}</span></li>)}
        </ul>
      </div>
    </details>
  );
}

// a lead-in the plan may already end with a period or colon; we add our own
const label = (s) => String(s || "").trim().replace(/[.:;]+$/, "");

export function ReviewerNotes({ c }) {
  const P = c.plan;
  const List = ({ title, items, render }) => (
    <div className="note">
      <h3>{title}</h3>
      <ul>{items.length ? items.map((x, i) => <li key={i}>{render(x)}</li>) : <li className="muted">Nothing.</li>}</ul>
    </div>
  );
  return (
    <section className="card">
      <h2>Notes for you</h2>
      <p className="why">These never appear in the report.</p>
      {P.judgment_calls.length > 0 && (
        <div className="callout note" style={{ marginBottom: 14 }}>
          <h3>Choices you may disagree with</h3>
          <ul>{P.judgment_calls.map((j, i) => <li key={i}><b>{label(j.call)}.</b> <Rich text={j.reasoning} /></li>)}</ul>
        </div>
      )}
      <div className="notes-grid">
        <List title="What the report promises the team will do" items={P.team_commitments} render={(t) => t} />
        <List title="Left out on purpose" items={P.left_out} render={(l) => <>{label(l.item)}: {l.why} <span className="muted">→ {l.route_to.replace(/_/g, " ")}</span></>} />
        <List title="Data excluded or adjusted" items={P.audit_notes} render={(a) => `${label(a.what)}: ${a.handling}`} />
        <List title="What the analysis couldn't check" items={P.gaps} render={(g) => g} />
      </div>
    </section>
  );
}

export function SomethingOff({ c }) {
  return (
    <section className="card">
      <h2>Plan feedback</h2>
      <p className="why">Something wrong in what the report says? Describe it. Only the parts of the plan your note is about are redone (plus anything that depends on them); everything else stays exactly as it is. You'll see what changed.</p>
      <textarea placeholder="e.g. Her husband is home now, so the hospital stay doesn't need mentioning." value={c.ui.note} onChange={(e) => c.setNote(e.target.value)} aria-label="Plan feedback" />
      <div style={{ marginTop: 8 }}>
        <button className="ghost" disabled={!c.ui.note.trim()} onClick={c.reviseWithNote}>Redo with this note</button>
      </div>
    </section>
  );
}
